import { ArrowLeft } from 'lucide-react';
import type { DayQualityFrontView } from '@obraplan/core/application/use-cases/quality/quality-use-cases';
import type { AttachmentUsage } from '@obraplan/core/application/use-cases/planning/attachment-use-cases';
import type { Attachment } from '@obraplan/core/domain/attachments/attachment';
import {
  NON_CONFORMITY_STATUS_LABEL,
  ownerKeyOf,
  type NonConformity,
} from '@obraplan/core/domain/quality/non-conformity';
import { describeChecklist } from '@obraplan/core/domain/quality/service-checklist';
import {
  nonConformitiesOfAnswer,
  VERIFICATION_RESULT_LABEL,
  VERIFICATION_RESULTS,
} from '@obraplan/core/domain/quality/service-verification';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/format';
import { useLive } from '@/lib/use-live';
import { placeOptions, useNcPlaces, type PlacesOfDay } from './nc-places';
import { NonConformityFields, type StepMeasurement } from './NonConformityFields';
import { PendingNonConformities } from './PendingNonConformities';

import {
  EMPTY_ANSWER,
  type AnswerDraft,
  type ChecklistDraft,
  type FrontDraft,
  type ServiceDraft,
} from './quality-verification-draft';

/**
 * O segundo passo da medição: a verificação de qualidade de **uma** frente (Fase 19, fatia 3).
 *
 * É um passo, e não um bloco sob o campo de quantidade, porque não cabia: N serviços ×
 * M fichas × sete itens × três respostas empurrariam o botão de salvar e a galeria de
 * fotos para fora de qualquer tablet. Quem mede vê a quantidade e as fotos como sempre
 * viu, e entra aqui uma frente por vez.
 *
 * O que se pergunta, na ordem do canteiro: **qual serviço executou hoje** (a declaração
 * que satisfaz a RN-218 — inclusive o serviço sem ficha), depois **qual ficha se aplica**
 * (um serviço pode ter duas), depois **cada item** com três respostas. Reprovar abre o
 * campo do que foi encontrado, porque é isso que vira a não conformidade (RN-220).
 *
 * Componente puro: recebe o rascunho e devolve o rascunho. Quem guarda é o diálogo, que
 * o chaveia pela conferência aberta — e não pela linha, que muda a cada revalidação.
 */

// ── O componente ────────────────────────────────────────────────────────────────

/**
 * As fotos das NCs já abertas — a coluna "Fotos (Quando Necessário)" do FORM.30 (fatia 6).
 *
 * Só depois de gravada a NC tem id e pode ter foto; por isso a galeria aparece sob o item
 * que já está reprovado e numerado, e não no rascunho.
 */
export interface NonConformityPhotos {
  readonly of: (
    nonConformityId: string,
  ) => { attachments: readonly Attachment[]; usage: AttachmentUsage | undefined } | undefined;
  readonly canAttach: boolean;
  readonly canRemove: boolean;
  readonly isPending: boolean;
  readonly resolveUrl: (attachmentId: string) => Promise<string | undefined>;
  readonly onAttach: (nonConformityId: string, files: readonly File[]) => void;
  readonly onRemove: (attachmentId: string) => void;
}

export interface QualityVerificationStepProps {
  front: DayQualityFrontView;
  draft: FrontDraft;
  nonConformities: readonly NonConformity[];
  /**
   * O serviço que **já foi escolhido** antes de abrir — o do lançamento do RDO.
   *
   * Quando ele vem, a pergunta "qual serviço executou hoje?" desaparece: ela já foi
   * respondida no formulário, e repeti-la é pedir a mesma escolha duas vezes. A lista
   * mostra só esse serviço, com as fichas dele.
   *
   * Ausente é a conferência, onde a pergunta continua valendo: lá se olha o dia inteiro da
   * pessoa, e ela pode ter feito mais de um serviço na mesma frente.
   */
  serviceId?: string;
  disabled: boolean;
  photos?: NonConformityPhotos | undefined;
  /** De quem é cada NC do dia — "Rafael Souza" (Fase 37, `DayQualityView`). */
  nonConformityOwners?: Readonly<Record<string, string>> | undefined;
  /**
   * A medição desta tela em cada serviço (Fase 37): o dono das NCs que ela abrir, o que mede
   * e a unidade. Ausente: o passo não sabe de medição, e a quantidade fica sem teto na tela.
   */
  measurementOf?: ((serviceId: string) => StepMeasurement | undefined) | undefined;
  /**
   * As NCs abertas de dias anteriores (Fase 37, D10 — `DayQualityView.pendingNonConformities`).
   * As desta frente, nos serviços da tela, viram o aviso da remedição no alto do passo.
   */
  pendingNonConformities?: readonly NonConformity[] | undefined;
  onChange: (next: FrontDraft) => void;
  onBack: () => void;
}

export function QualityVerificationStep({
  front,
  draft,
  nonConformities,
  serviceId,
  disabled,
  photos,
  nonConformityOwners,
  measurementOf,
  pendingNonConformities,
  onChange,
  onBack,
}: QualityVerificationStepProps) {
  const vivas = nonConformities.filter((nc) => nc.status !== 'CANCELLED');
  const ferramentas = useNcPlaces();

  /*
   * A lista de serviços da tela.
   *
   * Com o serviço já escolhido no formulário, é só ele: perguntar de novo seria pedir a
   * mesma escolha duas vezes. Serviço escolhido que não está na frente — disciplina trocada
   * entre abrir e responder — cai na lista inteira, que é o comportamento de sempre.
   */
  const escolhido =
    serviceId === undefined
      ? undefined
      : front.services.find((service) => service.serviceId === serviceId);
  const servicos = escolhido === undefined ? front.services : [escolhido];

  // D9 — os lugares do dia, lidos só para o serviço com item reprovado por esta medição: a
  // frente sem reprovação não paga pelas duas leituras.
  const comReprovacao = servicos
    .filter((service) =>
      Object.values(draft[service.serviceId]?.checklists ?? {}).some((ficha) =>
        Object.values(ficha.answers).some(
          (answer) => answer.result === 'REJECTED' && (!answer.fromOthers || answer.own),
        ),
      ),
    )
    .map((service) => service.serviceId);
  const lugaresDoDia = useLive(async () => {
    if (ferramentas === undefined || comReprovacao.length === 0) return undefined;
    const lidos = await Promise.all(
      comReprovacao.map(
        async (id) =>
          [
            id,
            await ferramentas.loadPlaces({ activityId: front.activityId, serviceId: id }),
          ] as const,
      ),
    );
    return new Map<string, PlacesOfDay | undefined>(lidos);
  }, [front.activityId, comReprovacao.join('|'), ferramentas !== undefined]);

  // D10 — as NCs abertas de outros dias nesta frente, nos serviços desta tela.
  const pendentes = (pendingNonConformities ?? []).filter(
    (nc) =>
      nc.activityId === front.activityId &&
      servicos.some((service) => service.serviceId === nc.serviceId),
  );

  const alterarServico = (serviceId: string, mudanca: Partial<ServiceDraft>) => {
    const atual = draft[serviceId] ?? { selected: false, checklists: {} };
    onChange({ ...draft, [serviceId]: { ...atual, ...mudanca } });
  };

  const alterarFicha = (
    serviceId: string,
    checklistId: string,
    mudanca: Partial<ChecklistDraft>,
  ) => {
    const servico = draft[serviceId] ?? { selected: false, checklists: {} };
    const ficha = servico.checklists[checklistId] ?? { applies: false, answers: {} };
    onChange({
      ...draft,
      [serviceId]: {
        ...servico,
        checklists: { ...servico.checklists, [checklistId]: { ...ficha, ...mudanca } },
      },
    });
  };

  const responder = (
    serviceId: string,
    checklistId: string,
    itemId: string,
    mudanca: Partial<AnswerDraft>,
  ) => {
    const ficha = draft[serviceId]?.checklists[checklistId] ?? { applies: true, answers: {} };
    const resposta = ficha.answers[itemId] ?? EMPTY_ANSWER;
    alterarFicha(serviceId, checklistId, {
      answers: { ...ficha.answers, [itemId]: { ...resposta, ...mudanca } },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onBack} aria-label="Voltar às quantidades">
          <ArrowLeft aria-hidden="true" />
          Voltar
        </Button>
      </div>

      <div>
        <h3 className="text-base font-semibold">{front.label}</h3>
        <p className="text-sm text-(--color-content-muted)">
          Verificação de qualidade · {front.disciplineName}
        </p>
      </div>

      <PendingNonConformities
        nonConformities={pendentes}
        serviceName={(id) => front.services.find((service) => service.serviceId === id)?.name}
        unitName={(id) => measurementOf?.(id)?.unitName}
        disabled={disabled}
        onResolve={ferramentas?.resolvePending}
      />

      {servicos.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">
          Sem serviço cadastrado para esta disciplina. A frente mede como sempre mediu; para
          verificar a qualidade, cadastre os serviços em Quantitativo → Serviços da obra.
        </p>
      ) : (
        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-sm font-medium">
            {serviceId === undefined ? 'Qual serviço executou hoje?' : 'A checklist do serviço'}
          </legend>
          {servicos.map((service) => {
            const serviceDraft = draft[service.serviceId];
            const selected = serviceDraft?.selected === true;
            const verification = service.verification;
            return (
              <div
                key={service.serviceId}
                className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3"
              >
                <label className="flex min-h-11 items-center gap-2 text-sm md:min-h-9">
                  {escolhido === undefined && (
                    <input
                      type="checkbox"
                      aria-label={service.name}
                      disabled={disabled}
                      checked={selected}
                      onChange={(event) => {
                        alterarServico(service.serviceId, { selected: event.target.checked });
                      }}
                    />
                  )}
                  <span className="font-medium">{service.name}</span>
                  <span className="text-xs text-(--color-content-muted)">
                    {service.checklists.length === 0
                      ? 'sem checklist'
                      : service.checklists.length === 1
                        ? '1 checklist'
                        : `${String(service.checklists.length)} checklists`}
                  </span>
                </label>

                {verification !== undefined && (
                  <p className="flex flex-wrap items-center gap-2 text-xs text-(--color-content-muted)">
                    <Badge tone={verification.summary.rejected > 0 ? 'warn' : 'ok'}>
                      {verification.summary.approved}/{verification.summary.total} aprovados
                      {verification.summary.rejected > 0 &&
                        ` · ${String(verification.summary.rejected)} reprovado(s)`}
                    </Badge>
                    verificado por {verification.verifiedByName} em{' '}
                    {formatDateTime(verification.verifiedAt)}
                    {selected && ' — responder de novo substitui'}
                  </p>
                )}

                {selected &&
                  service.checklists.map((checklist) => {
                    const checklistDraft = serviceDraft?.checklists[checklist.checklistId];
                    const applies = checklistDraft?.applies === true;
                    return (
                      <div key={checklist.checklistId} className="flex flex-col gap-2 pl-1">
                        <label className="flex min-h-11 items-center gap-2 text-sm md:min-h-9">
                          <input
                            type="checkbox"
                            aria-label={`Aplicar ${describeChecklist(checklist)}`}
                            disabled={disabled}
                            checked={applies}
                            onChange={(event) => {
                              alterarFicha(service.serviceId, checklist.checklistId, {
                                applies: event.target.checked,
                              });
                            }}
                          />
                          {describeChecklist(checklist)}
                        </label>

                        {applies && (
                          <ol className="flex flex-col gap-2">
                            {checklist.items.map((item, index) => {
                              const answer = checklistDraft?.answers[item.id];
                              const medicao = measurementOf?.(service.serviceId);
                              const dono = medicao?.ownerKey;
                              const respostaGravada = verification?.checklists
                                .find((c) => c.checklistId === checklist.checklistId)
                                ?.answers.find((a) => a.itemId === item.id);
                              const doItem =
                                verification === undefined
                                  ? []
                                  : nonConformitiesOfAnswer(
                                      vivas,
                                      verification.id,
                                      checklist.checklistId,
                                      respostaGravada ?? { itemId: item.id },
                                    );
                              // RN-474 — a NC desta medição, e as das outras no mesmo item.
                              const propria = doItem.find((nc) => ownerKeyOf(nc) === dono);
                              const deOutras = doItem.filter((nc) => ownerKeyOf(nc) !== dono);
                              const travado = answer?.fromOthers === true;
                              return (
                                <li
                                  key={item.id}
                                  className="flex flex-col gap-1 rounded-md bg-(--color-surface-muted) px-3 py-2"
                                >
                                  <p className="text-sm font-medium">
                                    {index + 1}. {item.label}
                                  </p>
                                  {(item.method !== undefined || item.tolerance !== undefined) && (
                                    <p className="text-xs text-(--color-content-muted)">
                                      {item.method}
                                      {item.tolerance !== undefined &&
                                        `${item.method === undefined ? '' : ' · '}Tolerância: ${item.tolerance}`}
                                    </p>
                                  )}
                                  <div
                                    role="radiogroup"
                                    aria-label={item.label}
                                    className="flex flex-wrap gap-x-4 gap-y-1"
                                  >
                                    {VERIFICATION_RESULTS.map((result) => (
                                      <label
                                        key={result}
                                        className="flex min-h-11 items-center gap-1.5 text-sm md:min-h-9"
                                      >
                                        <input
                                          type="radio"
                                          name={`${checklist.checklistId}-${item.id}`}
                                          value={result}
                                          // O item com NC de outra medição fica reprovado (RN-474).
                                          disabled={disabled || (travado && result !== 'REJECTED')}
                                          checked={answer?.result === result}
                                          onChange={() => {
                                            responder(
                                              service.serviceId,
                                              checklist.checklistId,
                                              item.id,
                                              { result },
                                            );
                                          }}
                                        />
                                        {VERIFICATION_RESULT_LABEL[result]}
                                      </label>
                                    ))}
                                  </div>
                                  {answer?.result === 'REJECTED' && deOutras.length > 0 && (
                                    <div className="flex flex-col gap-1.5">
                                      <ul
                                        aria-label={`Não conformidades de outras medições — ${item.label}`}
                                        className="flex flex-col gap-1 text-xs"
                                      >
                                        {deOutras.map((nc) => (
                                          <li key={nc.id} className="text-(--color-content-muted)">
                                            <span className="font-medium text-(--color-content)">
                                              NC nº {nc.number}
                                            </span>
                                            {nonConformityOwners?.[nc.id] !== undefined &&
                                              ` · ${nonConformityOwners[nc.id]!}`}
                                            {nc.quantity !== undefined &&
                                              ` · ${nc.quantity.toLocaleString('pt-BR')}${medicao?.unitName === undefined ? '' : ` ${medicao.unitName}`}`}
                                            {` · ${NON_CONFORMITY_STATUS_LABEL[nc.status].toLowerCase()} — ${nc.description}`}
                                          </li>
                                        ))}
                                      </ul>
                                      <label className="flex min-h-11 items-center gap-2 text-sm md:min-h-9">
                                        <input
                                          type="checkbox"
                                          disabled={disabled}
                                          checked={answer.own}
                                          onChange={(event) => {
                                            responder(
                                              service.serviceId,
                                              checklist.checklistId,
                                              item.id,
                                              { own: event.target.checked },
                                            );
                                          }}
                                        />
                                        Esta medição também encontrou não conformidade aqui
                                      </label>
                                    </div>
                                  )}
                                  {answer?.result === 'REJECTED' &&
                                    (deOutras.length === 0 || answer.own) && (
                                      <NonConformityFields
                                        itemLabel={item.label}
                                        answer={answer}
                                        disabled={disabled}
                                        measurement={medicao}
                                        saved={propria}
                                        photos={photos}
                                        placeOptions={
                                          ferramentas === undefined
                                            ? undefined
                                            : placeOptions({
                                                ofDay: lugaresDoDia?.get(service.serviceId),
                                                draftPieces: medicao?.draftPieces,
                                                draftRooms: medicao?.draftRooms,
                                                saved: propria,
                                              })
                                        }
                                        placesLoading={
                                          ferramentas !== undefined && lugaresDoDia === undefined
                                        }
                                        onChange={(mudanca) => {
                                          responder(
                                            service.serviceId,
                                            checklist.checklistId,
                                            item.id,
                                            mudanca,
                                          );
                                        }}
                                      />
                                    )}
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </fieldset>
      )}
    </div>
  );
}
